import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Card, CardImg, CardBody } from 'reactstrap';
import ImageTypesField from '../ImageTypesField/ImageTypesField';
import LangsField from '../LangsField/LangsField';
import CardIconAction from '../CardIconAction/CardIconAction';
import CardIconActions from '../CardIconAction/CardIconActions';
import { Field, change as changeFormValue } from 'redux-form';
import { Utils } from '../../services';
import { show_app_toast } from '../../services/appToast';
import { useSmartImageUpload } from '../../services/image';
import { sanitize_theme_logo_data_uri } from '../../services/image/sanitizeThemeLogoSvg';

import './InlineImageField.css';
import '../CardIconAction/CardIconAction.scss';

const SETTING_FORM = 'settingForm';

const InlineImageField = ({
    name,
    image,
    hideOptions,
    hideType,
    preset = 'setting',
    onDropImage,
    onDuplicate,
    onRemove,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const show_lang = Utils.langs().length > 1;
    const [ data, setData ] = useState(image?.rfc2397_data);
    const fieldOnChangeRef = useRef(null);
    const show_options = !hideOptions;
    const show_type = show_options && !hideType;

    useEffect(() => {
        setData(image?.rfc2397_data);
    }, [ image ]);

    const dataComponent = ({ input }) => {
        fieldOnChangeRef.current = input.onChange;
        return <input type="hidden" name={ input.name } value={ data } />;
    };

    const onProcessed = useCallback((dataUrl) => {
        setData(dataUrl);
        // Prefer redux-form `change` so nested logo[i].rfc2397_data stays in sync
        // even if the hidden Field remounts (inline `component` identity).
        dispatch(changeFormValue(SETTING_FORM, `${name}.rfc2397_data`, dataUrl));
        if (fieldOnChangeRef.current)
            fieldOnChangeRef.current(dataUrl);
        if (onDropImage)
            setTimeout(() => onDropImage(), 0);
    }, [dispatch, name, onDropImage]);

    const { handleFiles } = useSmartImageUpload({
        preset,
        imageType: image?.type,
        onProcessed,
    });

    const onDrop = useCallback((acceptedFiles) => {
        handleFiles(acceptedFiles);
    }, [handleFiles]);

    const onDropRejected = useCallback(() => {
        show_app_toast(t('image_upload.invalid_file'), { variant: 'error' });
    }, [t]);

    // Do not restrict mobileThemeLogo to SVG-only: that silently rejected PNG/WebP
    // (no onDrop, no toast). Pipeline keeps SVG as SVG; non-SVG still shows
    // settings.mobile.theme.logo_not_svg_help under the card.
    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        onDropRejected,
    });

    const is_non_svg_logo = preset === 'mobileThemeLogo'
        && data
        && !/^data:image\/svg\+xml/i.test(data);

    const preview_src = preset === 'mobileThemeLogo'
        ? (sanitize_theme_logo_data_uri(data) || data)
        : data;

    return (
        <div className="InlineImageField">
            <Card>
                <div className="img-wrapper">
                    {(onDuplicate || onRemove) && data && (
                        <CardIconActions className="CardIconActions--overlay-media">
                            { onDuplicate && (
                                <CardIconAction
                                    id={`duplicate-${name}`}
                                    icon="fa-copy"
                                    title={t('common.duplicate')}
                                    onClick={onDuplicate}
                                />
                            )}
                            { onRemove && (
                                <CardIconAction
                                    id={`delete-${name}`}
                                    icon="fa-trash"
                                    danger
                                    title={t('common.card_action_delete_poster')}
                                    onClick={onRemove}
                                />
                            )}
                        </CardIconActions>
                    )}
                    <div {...getRootProps({className: 'dropzone'})}>
                        <input {...getInputProps()} />
                        <p>{ t('common.drag_one_file') }</p>
                        { preview_src && (
                            <div className="InlineImageField__imageWrap">
                                <CardImg top src={ preview_src } alt="Preview" />
                            </div>
                        )}
                    </div>
                    <Field component={ dataComponent } type="hidden" className="form-control" name={ `${name}.rfc2397_data` } />
                </div>
                { is_non_svg_logo ? (
                    <CardBody className="pt-2 pb-2">
                        <p className="text-warning small mb-0">
                            { t('settings.mobile.theme.logo_not_svg_help') }
                        </p>
                    </CardBody>
                ) : null }
                { (show_type || ((show_options || hideType) && show_lang)) && (
                <CardBody>
                    { show_type && (
                        <ImageTypesField width="100%" component="input" type="text" className="form-control" name={`${name}.type`} usage="setting" />
                    ) }
                    { (show_options || hideType) && show_lang && (
                        <LangsField name={`${name}.lang`} onlyUiLocales />
                    ) }
                </CardBody>
                )}
            </Card>
        </div>
    );
}

export default InlineImageField;
